import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { take } from 'rxjs/operators';
import * as Papa from 'papaparse';
import { saveAs } from 'file-saver';
import Me from '../helpers/me';
import App from '../helpers/app';
import Api from '../helpers/api';
import { ExcelService } from '../helpers/excel.service';

@Component({
  selector: 'app-exato-consulta',
  templateUrl: './exato-consulta.component.html',
  styleUrls: ['./exato-consulta.component.scss']
})
export class ExatoConsultaComponent implements OnInit {
  apiCall = this.api.new().silent();

  // Tamanho do lote enviado por requisição (mesma lógica de 30 em 30 das demais telas).
  readonly TAMANHO_LOTE = 30;

  // Entrada de CPFs
  cpfsTexto = '';
  arquivoNome = '';
  origem: 'manual' | 'csv' = 'manual';
  cpfs: string[] = [];

  // Estado do processamento
  consultaEmAndamento = false;
  processados = 0;
  total = 0;
  loteAtual = 0;
  totalLotes = 0;

  // Histórico de consultas
  consultas: any[] = [];
  carregandoConsultas = false;
  paginaConsultas = 1;
  tamanhoPaginaConsultas = 10;
  consultaSelecionada: any = null;
  itensConsulta: any[] = [];
  carregandoItens = false;

  constructor(
    private me: Me,
    private app: App,
    private api: Api,
    private excelService: ExcelService,
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.carregarConsultas();
  }

  /** Carrega o histórico de consultas (cabeçalhos). */
  async carregarConsultas() {
    this.carregandoConsultas = true;
    try {
      const lista = await this.apiCall.get('exato/consultas').pipe(take(1)).toPromise();
      this.consultas = Array.isArray(lista) ? lista : [];
      this.paginaConsultas = 1;
    } catch (e) {
      this.consultas = [];
    }
    this.carregandoConsultas = false;
  }

  /** Abre o modal com os itens (detalhes) de uma consulta. */
  async abrirDetalhes(consulta: any, modalTemplate: any) {
    this.consultaSelecionada = consulta;
    this.itensConsulta = [];
    this.carregandoItens = true;
    this.modalService.open(modalTemplate, { size: 'xl', centered: true, scrollable: true });
    try {
      const itens = await this.apiCall.get(`exato/consultas/${consulta.id}/itens`).pipe(take(1)).toPromise();
      this.itensConsulta = Array.isArray(itens) ? itens : [];
    } catch (e) {
      this.itensConsulta = [];
    }
    this.carregandoItens = false;
  }

  /** Mapeia os itens (formato do banco) para as colunas exibidas/exportadas. */
  private mapearItens(itens: any[]): any[] {
    return (itens || []).map((r) => ({
      'Nome': r.nome || '',
      'CPF': r.cpf || '',
      'Prova de Vida': r.prova_de_vida || '',
      'Consta Óbito': r.consta_obito === 1 ? 'Sim' : (r.consta_obito === 0 ? 'Não' : ''),
      'Situação Cadastral': r.situacao_cadastral || '',
      'Data de Óbito': r.data_obito || '',
      'Nome na Receita Federal': r.nome_receita_federal || '',
      'Data de Nascimento': r.data_nascimento || '',
      'Observação': r.erro || '',
    }));
  }

  /** Baixa novamente os resultados de uma consulta em CSV. */
  async baixarCsv(consulta: any) {
    let itens = [];
    try {
      const resp = await this.apiCall.get(`exato/consultas/${consulta.id}/itens`).pipe(take(1)).toPromise();
      itens = Array.isArray(resp) ? resp : [];
    } catch (e) {
      itens = [];
    }

    if (itens.length === 0) {
      this.app.alert('Consulta Exato', 'Esta consulta não possui itens para exportar.', 'warning');
      return;
    }

    const csv = Papa.unparse(this.mapearItens(itens));
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `consulta_exato_${consulta.id}.csv`);
  }

  /** Atualiza a lista de CPFs a partir do texto digitado (origem manual). */
  aoDigitarCpfs() {
    this.origem = 'manual';
    this.arquivoNome = '';
    this.cpfs = this.normalizarCpfs(this.cpfsTexto);
  }

  /** Lê um CSV e extrai os CPFs (origem csv). */
  onArquivoSelecionado(event: any) {
    const file: File = event?.target?.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const texto = (reader.result as string) || '';
      this.origem = 'csv';
      this.arquivoNome = file.name;
      this.cpfsTexto = '';
      this.cpfs = this.normalizarCpfs(texto);
      this.cdr.detectChanges();
      if (this.cpfs.length === 0) {
        this.app.alert('Consulta Exato', 'Nenhum CPF válido (11 dígitos) foi encontrado no arquivo.', 'warning');
      }
    };
    // Mesmo encoding usado na tela de importações.
    reader.readAsText(file as Blob, 'ISO-8859-1');
    // Permite reselecionar o mesmo arquivo depois.
    event.target.value = '';
  }

  /**
   * Extrai CPFs de um texto (vírgula, ponto e vírgula, espaço ou quebra de linha),
   * mantém apenas os de 11 dígitos e remove duplicados.
   */
  normalizarCpfs(texto: string): string[] {
    if (!texto) {
      return [];
    }
    const tokens = String(texto).split(/[\s,;]+/);
    const vistos = new Set<string>();
    const resultado: string[] = [];
    for (const token of tokens) {
      const digitos = (token || '').replace(/\D+/g, '');
      if (digitos.length === 11 && !vistos.has(digitos)) {
        vistos.add(digitos);
        resultado.push(digitos);
      }
    }
    return resultado;
  }

  limpar() {
    this.cpfsTexto = '';
    this.arquivoNome = '';
    this.origem = 'manual';
    this.cpfs = [];
  }

  /** Percentual de progresso (0–100), com guarda para total zero. */
  progressoPercentual(): number {
    if (!this.total) {
      return 0;
    }
    return Math.round((this.processados / this.total) * 100);
  }

  /**
   * Força a atualização da view e cede o controle ao browser por um tick, garantindo
   * que a barra de progresso seja repintada entre um lote e outro.
   */
  private renderizarProgresso(): Promise<void> {
    this.cdr.detectChanges();
    return new Promise((resolve) => setTimeout(resolve, 0));
  }

  async gerarProvaDeVida() {
    const self = this;
    if (self.consultaEmAndamento || self.cpfs.length === 0) {
      return;
    }

    const idUsuario =
      (self.me.currentUserValue && (self.me.currentUserValue as any).user && (self.me.currentUserValue as any).user.id) ||
      (self.me.currentUserValue && (self.me.currentUserValue as any).id) ||
      null;

    self.consultaEmAndamento = true;
    self.processados = 0;
    self.total = self.cpfs.length;
    self.loteAtual = 0;
    self.totalLotes = Math.ceil(self.total / self.TAMANHO_LOTE);
    await self.renderizarProgresso();

    // 1) Cria o cabeçalho da consulta no banco.
    let consultaId = null;
    try {
      const criada = await self.apiCall
        .post('exato/consultas', {
          id_usuario: idUsuario,
          origem: self.origem,
          total_cpfs: self.total,
        })
        .pipe(take(1))
        .toPromise();
      consultaId = criada && criada.consulta_id != null ? criada.consulta_id : null;
    } catch (e) {
      consultaId = null;
    }

    // 2) Processa em lotes de 30, acumulando os resultados.
    const acumulado = [];
    try {
      for (let inicio = 0; inicio < self.total; inicio += self.TAMANHO_LOTE) {
        const chunk = self.cpfs.slice(inicio, inicio + self.TAMANHO_LOTE);

        const call = self.api.new().silent();
        call.timeout = 5 * 60 * 1000;

        const relatorio = await call
          .post('exato/prova-de-vida', {
            cpfs: chunk,
            consulta_id: consultaId,
          })
          .pipe(take(1))
          .toPromise();

        if (!relatorio || !Array.isArray(relatorio.resultados)) {
          await self.abortarComErro(consultaId, self.loteAtual + 1);
          return;
        }

        acumulado.push(...relatorio.resultados);
        self.processados = acumulado.length;
        self.loteAtual++;
        await self.renderizarProgresso();
      }
    } catch (e) {
      await self.abortarComErro(consultaId, self.loteAtual + 1);
      return;
    }

    // 3) Finaliza a consulta (status + contadores) — best-effort.
    const contadores = {
      total_vivos: acumulado.filter((r) => r.prova_de_vida === 'Vivo').length,
      total_obitos: acumulado.filter((r) => r.prova_de_vida === 'Óbito').length,
      total_indeterminados: acumulado.filter((r) => r.prova_de_vida === 'Indeterminado').length,
      total_erros: acumulado.filter((r) => r.erro).length,
    };
    if (consultaId) {
      try {
        await self.apiCall
          .put(`exato/consultas/${consultaId}`, { status: 'concluida', ...contadores })
          .pipe(take(1))
          .toPromise();
      } catch (e) {
        // não bloqueia o export
      }
    }

    self.consultaEmAndamento = false;
    self.cdr.detectChanges();

    const dados = acumulado.map((r) => ({
      'Nome': r.nome || '',
      'CPF': r.cpf || '',
      'Prova de Vida': r.prova_de_vida || '',
      'Consta Óbito': r.consta_obito === true ? 'Sim' : (r.consta_obito === false ? 'Não' : ''),
      'Situação Cadastral': r.situacao_cadastral || '',
      'Data de Óbito': r.data_obito || '',
      'Nome na Receita Federal': r.nome_receita_federal || '',
      'Data de Nascimento': r.data_nascimento || '',
      'Observação': r.erro || '',
    }));

    self.excelService.exportAsExcelFile(JSON.parse(JSON.stringify(dados)), 'consulta_exato');

    // Atualiza o histórico com a consulta recém-concluída.
    self.carregarConsultas();
  }

  /** Interrompe o processo, marca a consulta como erro e descarta (sem export). */
  private async abortarComErro(consultaId: any, lote: number) {
    this.consultaEmAndamento = false;
    this.cdr.detectChanges();
    if (consultaId) {
      try {
        await this.apiCall
          .put(`exato/consultas/${consultaId}`, { status: 'erro' })
          .pipe(take(1))
          .toPromise();
      } catch (e) {
        // ignora
      }
    }
    this.app.alert(
      'Consulta Exato',
      `Falha ao processar o lote ${lote}. O processamento foi interrompido e nenhum arquivo foi gerado. Tente novamente.`,
      'error'
    );
  }
}
